/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/api/sources.js
 *
 * Purpose: React Query hooks for listing, fetching, saving, and bulk-importing sources.
 *
 * Scope: Source list/detail views, source attachments, and the source import template/
 *     preview/import workflow.
 *
 * Tags: customer-portal, sources
 *
 * Overview: `useSourceList` runs an infinite query over the source list endpoint and
 *     flattens the paginated results into a single array. `useSource` fetches a single
 *     source by the `id` route param. `useSourceAttachments` and `useSourceAttachmentUrl`
 *     fetch attachment metadata and a download URL for a source. `useSourceUpsert` creates
 *     or updates a source, invalidating the relevant list/detail caches and notifying on
 *     success. `useSourceImportTemplate` downloads an import template built from the first
 *     page of existing sources (capped at `MAX_TEMPLATE_SOURCES`, with a notice if more
 *     exist), while `useSourceImportPreview` and `useSourceImport` preview and execute a
 *     bulk source import from an uploaded file.
 *
 * Exports: useSourceList, useSource, useSourceAttachments, useSourceAttachmentUrl,
 *     useSourceUpsert, useSourceImportTemplate, useSourceImportPreview, useSourceImport
 */

import isEmpty from 'lodash/isEmpty';
import { useCallback } from 'react';
import { NotificationManager } from 'react-notifications';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { EntityType, QueryKeys } from 'shared/constants/entityTypes';
import { axiosinstance, Sources } from 'shared/utils/api';
import { queryClient } from 'shared/utils/queryClient';
import { useEntityDisplayName } from '../misc/fields';
import { useRequestParams } from './misc';

const MAX_TEMPLATE_SOURCES = 150;

const entityName = QueryKeys.SOURCE;
const baseUrl = `/v1/${entityName}`;

export function useSourceList(reqParams = {}, queryOptions = {}) {
  const params = useRequestParams(reqParams);
  const queryKey = [entityName, 'list', params];
  const { data, ...queryResult } = useInfiniteQuery(
    queryKey,
    async ({ pageParam: page = 1 }) => {
      const res = await axiosinstance.get(`/v1/${entityName}`, {
        params: { ...params, page },
      });
      return res.data;
    },
    {
      getNextPageParam: ({ page_number: current, total_pages: total }) => {
        const nextPage = current < total ? current + 1 : null;
        return nextPage ?? undefined;
      },
      ...queryOptions,
    }
  );

  function combinePagesData(data) {
    const initialValues = [];
    return !isEmpty(data) && data.pages && !isEmpty(data.pages)
      ? data.pages.reduce((acc, page) => [...acc, ...page.data], initialValues)
      : initialValues;
  }

  return { ...queryResult, data: combinePagesData(data) };
}

/**
 * @return {import('react-query').UseQueryResult & {
 *    data: Record<string, any>;
 * }}
 */
export function useSource(queryOptions = {}) {
  const { id } = useParams();
  const sourceId = parseInt(id);
  const fetchSource = () =>
    axiosinstance
      .get(`${baseUrl}/${sourceId}`)
      .then(({ data: res }) => res.data);
  const { data, ...queryResult } = useQuery([entityName, sourceId], fetchSource, queryOptions);

  return {
    data: data || {},
    ...queryResult,
  };
}

export const useSourceAttachments = id => {
	const fetchAttachments = () =>
		id
			? axiosinstance
					.get(`/v1/source/attachments?ids=${id}`)
					.then(res => res.data)
			: [];
	const { data, isLoading, error, refetch, isFetching } = useQuery(
		['sourceAttachments', parseInt(id)],
		fetchAttachments
	);

	return {
		data,
		isLoading,
		error,
		refetch,
		isFetching,
	};
};

export const useSourceAttachmentUrl = assetId => {
  const fetchAttachmentUrl = () =>
    assetId
      ? axiosinstance
          .get(`/v1/asset/${assetId}/download`)
          .then(res => res.data)
      : null;
  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['sourceUrlAttachment', parseInt(assetId)],
    fetchAttachmentUrl
  );

  return {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  };
};

export function useSourceUpsert() {
  const keysToInvalidate = [
    [entityName, 'list'],
    [entityName, 'options'],
  ];
  const [sourceSingular] = useEntityDisplayName(EntityType.SOURCE);

  const mutationFn = (source) =>
    (source.id ? Sources.update(source) : Sources.create(source)).then(
      (res) => res?.data
    );

  const { mutateAsync, ...mutationObject } = useMutation({
    mutationFn,
    onSuccess: (source, payload) => {
      const { id } = payload;
      const isUpdate = !!id;

      if (isUpdate) {
        const sourceId = parseInt(id);
        queryClient.invalidateQueries([entityName, sourceId]);
        queryClient.invalidateQueries([entityName, 'list'], {
          refetchInactive: true,
        });
      } else {
        keysToInvalidate.forEach((k) => queryClient.invalidateQueries(k));
      }

      NotificationManager.success(
        `${sourceSingular} successfully ${isUpdate ? 'updated' : 'created'}.`
      );
    },
  });

  return [mutateAsync, mutationObject];
}

export function useSourceImportTemplate() {
  const fetchTemplate = useCallback(async () => {
    const listRes = await axiosinstance.get(`/v1/${entityName}`, {
      params: { page_size: MAX_TEMPLATE_SOURCES, page: 1 },
    });
    const { data: sources = [], total_pages } = listRes.data || {};
    const entIds = sources.map((s) => s.id);

    if (total_pages > 1) {
      NotificationManager.info(
        `Your template includes the first ${MAX_TEMPLATE_SOURCES} sources.`,
        'Template limit',
        8000
      );
    }

    return await Sources.importTemplate(entIds);
  }, []);

  return { fetchTemplate };
}

export function useSourceImportPreview() {
  const { mutateAsync, ...mutationObject } = useMutation({
    mutationFn: (file) => Sources.importPreview(file),
    onError: () => {
      NotificationManager.error('An error occurred while previewing the file. Please try again.');
    },
  });

  return [mutateAsync, mutationObject];
}

export function useSourceImport() {
  const keysToInvalidate = [
    [entityName, 'list'],
    [entityName, 'options'],
  ];
  const [sourcePlural] = useEntityDisplayName(EntityType.SOURCE);

  const { mutateAsync, ...mutationObject } = useMutation({
    mutationFn: (file) => Sources.import(file),
    onSuccess: () => {
      keysToInvalidate.forEach((k) => queryClient.invalidateQueries(k));
      NotificationManager.success(`${sourcePlural} imported successfully.`);
    },
    onError: () => {
      NotificationManager.error('An error occurred while importing. Please try again.');
    },
  });

  return [mutateAsync, mutationObject];
}
